import type { EcommercePlans, EcommercePlan } from './types/ecommerce-plan';
import type { HostingPackages, HostingPackage } from './types/hosting-package';
import type { JSONObject, JSONValue } from './types/json';
import type { Subscription } from './types/subscription';
import config from './configuration/config';
import actions from './actions';
import selectors from './selectors';
import { snakeCaseObjectToCamelCase } from './utils/convert-case';
import {
  MOST_POPULAR_ECOMMERCE_PLANS,
  NON_PURCHASABLE_ECOMMERCE_PLANS,
} from './constants/subscription-type';
import messenger from './messenger';
import getUpdatedSubscriptionList from './helpers/get-updated-subscription-list';

type SubscriptionArgs = {
  statusIn?: string;
  typeIn?: string;
};

const serviceProvider = {
  async fetchCurrentPackage(): Promise<HostingPackage> {
    const subscriptionService = config.getSubscriptionService();
    const store = config.getStore();

    return subscriptionService.getCurrentPackage().then((response) => {
      const { body } = response;

      const hostingPackage = snakeCaseObjectToCamelCase<HostingPackage>(body);

      store.dispatch(actions.setCurrentPackage(hostingPackage));

      return hostingPackage;
    });
  },

  async fetchSubscriptions(options: SubscriptionArgs = {}): Promise<Subscription[]> {
    const subscriptionService = config.getSubscriptionService();
    const store = config.getStore();

    return subscriptionService.subscriptions(options).then((response) => {
      const { body } = response;

      store.dispatch(actions.setSubscriptions(body));

      return body;
    });
  },

  async fetchEcommercePlans(availableSubscriptionTypes: string[]): Promise<EcommercePlans> {
    // eslint-disable-next-line yola/@typescript-eslint/no-unnecessary-condition
    if (!availableSubscriptionTypes) {
      throw new Error(
        messenger.create('"availableSubscriptionTypes" are required for fetching ecommerce plans')
      );
    }

    const subscriptionService = config.getSubscriptionService();
    const store = config.getStore();

    return subscriptionService.getEcommercePlans().then((response) => {
      const { body } = response;

      const ecommercePlans = Object.entries(body).reduce(
        (result: EcommercePlans, [planType, planConfiguration]: [string, JSONValue]) => ({
          ...result,
          [planType]: {
            ...snakeCaseObjectToCamelCase<Omit<EcommercePlan, 'meta' | 'type' | 'available'>>(
              planConfiguration as JSONObject
            ),
            type: planType,
            available:
              availableSubscriptionTypes.includes(planType) &&
              !NON_PURCHASABLE_ECOMMERCE_PLANS.some(
                (nonPurchasablePlan) => nonPurchasablePlan === planType
              ),
            meta: {
              mostPopular: MOST_POPULAR_ECOMMERCE_PLANS.some(
                (mostPopularPlan) => mostPopularPlan === planType
              ),
            },
          },
        }),
        {}
      );

      store.dispatch(actions.setEcommercePlans(ecommercePlans));

      return ecommercePlans;
    });
  },

  async fetchPackages(partnerId: string): Promise<HostingPackages> {
    const partnerService = config.getPartnerService();
    const store = config.getStore();

    return partnerService.getPackages(partnerId).then((response) => {
      const { body } = response;

      const hostingPackages = Object.entries(body).reduce(
        (result: HostingPackages, [planType, planConfiguration]: [string, JSONValue]) => ({
          ...result,
          [planType]: {
            ...snakeCaseObjectToCamelCase<Omit<HostingPackage, 'type'>>(
              planConfiguration as JSONObject
            ),
            type: planType,
          },
        }),
        {}
      );

      store.dispatch(actions.setHostingPackages(hostingPackages));

      return hostingPackages;
    });
  },

  async reactivateSubscription(subscriptionId: string): Promise<Subscription> {
    const subscriptionService = config.getSubscriptionService();
    const store = config.getStore();

    return subscriptionService.reactivate(subscriptionId).then((response) => {
      const { body: updatedSubscription } = response;
      const previousSubscriptionList = selectors.getSubscriptionList(store.getState());

      if (previousSubscriptionList) {
        store.dispatch(
          actions.setSubscriptions(
            getUpdatedSubscriptionList(updatedSubscription, previousSubscriptionList)
          )
        );
      }

      return updatedSubscription;
    });
  },

  async cancelSubscription(subscriptionId: string): Promise<Subscription> {
    const subscriptionService = config.getSubscriptionService();
    const store = config.getStore();

    return subscriptionService.cancel(subscriptionId).then((response) => {
      const { body: updatedSubscription } = response;
      const previousSubscriptionList = selectors.getSubscriptionList(store.getState());

      if (previousSubscriptionList) {
        store.dispatch(
          actions.setSubscriptions(
            getUpdatedSubscriptionList(updatedSubscription, previousSubscriptionList)
          )
        );
      }

      return updatedSubscription;
    });
  },

  async rescheduleSubscription(subscriptionId: string): Promise<Subscription> {
    const subscriptionService = config.getSubscriptionService();
    const store = config.getStore();

    return subscriptionService.reschedule(subscriptionId).then((response) => {
      const { body: updatedSubscription } = response;
      const previousSubscriptionList = selectors.getSubscriptionList(store.getState());

      if (previousSubscriptionList) {
        store.dispatch(
          actions.setSubscriptions(
            getUpdatedSubscriptionList(updatedSubscription, previousSubscriptionList)
          )
        );
      }

      return updatedSubscription;
    });
  },

  async deprovisionSubscription(subscriptionId: string): Promise<Subscription> {
    const subscriptionService = config.getSubscriptionService();
    const store = config.getStore();

    return subscriptionService.deprovision(subscriptionId).then((response) => {
      const { body: updatedSubscription } = response;
      const previousSubscriptionList = selectors.getSubscriptionList(store.getState());

      if (previousSubscriptionList) {
        store.dispatch(
          actions.setSubscriptions(
            getUpdatedSubscriptionList(updatedSubscription, previousSubscriptionList)
          )
        );
      }

      return updatedSubscription;
    });
  },
};

export default serviceProvider;
